import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import {
  CloseIcon,
  CloudUploadIcon,
  InfoCircle,
  UploadedFileIcon,
} from '../../../assets/icons/index';
import LoaderIcon from '../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { BackendRoute } from '../../../config/index';
import { ApiService } from '../../../services/index';
import { getTranslation } from '../../../utils/get-translation.util';

export type UploadedFile = {
  abort: () => void;
  docLink: string;
  id: string;
  loadProgress: number;
  name: string;
};

type Props = {
  /** Optional "N files uploaded" line rendered above the list — design 86cayg7c8. */
  counter?: ReactNode;
  files: UploadedFile[];
  filesFirst?: boolean;
  onChange: (files: UploadedFile[]) => void;
};

const MAX_FILES = 20;
const MAX_FILE_SIZE = 1024 * 1024 * 10; // 10 MB

const ACCEPTED_TYPES = {
  'application/msword': ['.doc'],
  'application/pdf': ['.pdf'],
  'application/vnd.ms-excel': ['.xlsx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'text/csv': ['.csv'],
};

export const DividendFileUpload: FC<Props> = ({ counter, files, filesFirst, onChange }) => {
  const filesRef = useRef(files);
  filesRef.current = files;

  // Parallel uploads all mutate the same list, so every change is derived from the ref and written
  // back to it synchronously. Reading only the prop would hand each upload the pre-batch list —
  // the placeholders would overwrite one another and a multi-file pick would keep just one file.
  const applyFiles = useCallback(
    (update: (current: UploadedFile[]) => UploadedFile[]) => {
      const next = update(filesRef.current);
      filesRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  // Rejections are reported inside the upload block, where the user is looking, instead of a
  // toast in the page corner (86cat0jym).
  const [error, setError] = useState<null | string>(null);

  const isAtMax = files.length >= MAX_FILES;
  const message = error ?? (isAtMax ? getTranslation('MAXIMUM_FILES') : null);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejections: FileRejection[]) => {
      setError(null);

      if (rejections.length > 0) {
        const codes = new Set(rejections.flatMap((r) => r.errors.map((e) => e.code)));
        if (codes.has('file-too-large')) setError(getTranslation('MAX_FILE_SIZE_10_MB'));
        else if (codes.has('file-invalid-type')) setError(getTranslation('INVALID_FILE_TYPE'));
        else setError(getTranslation('FILE_SIZE_TOO_LARGE'));
      }

      if (acceptedFiles.length === 0) return;

      const remainingSlots = MAX_FILES - filesRef.current.length;
      if (acceptedFiles.length > remainingSlots) {
        setError(getTranslation('MAXIMUM_FILES'));
      }
      if (remainingSlots <= 0) return;

      const uploads = acceptedFiles.slice(0, remainingSlots).map((file) => {
        const { callback, abort } = ApiService.uploadFile<string>();
        const id = uuidv4();

        return {
          callback,
          file,
          id,
          placeholder: { abort, docLink: '', id, loadProgress: 1, name: file.name },
        };
      });

      applyFiles((current) => [...current, ...uploads.map(({ placeholder }) => placeholder)]);

      uploads.forEach(async ({ callback, file, id }) => {
        try {
          const link = await callback(
            { endpoint: BackendRoute.SHARE_CLASSES, routePath: ['file'] },
            ['file', file],
            {
              onAboard: () => {
                applyFiles((current) => current.filter((f) => f.id !== id));
              },
              onUploadProgress: (n) => {
                applyFiles((current) =>
                  current.map((f) => (f.id === id ? { ...f, loadProgress: n } : f)),
                );
              },
            },
          );

          if (!link) return;
          applyFiles((current) =>
            current.map((f) => (f.id === id ? { ...f, docLink: link, loadProgress: 100 } : f)),
          );
        } catch (uploadError) {
          // Server rejected the upload (e.g. file too large) — surface it instead of crashing.
          setError(
            uploadError instanceof Error
              ? uploadError.message
              : getTranslation('FILE_SIZE_TOO_LARGE'),
          );
          applyFiles((current) => current.filter((f) => f.id !== id));
        }
      });
    },
    [applyFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTED_TYPES,
    disabled: isAtMax,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    noClick: false,
    onDrop,
  });

  const handleDelete = useCallback(
    (id: string) => {
      const file = filesRef.current.find((f) => f.id === id);
      if (file && !file.docLink) file.abort();
      applyFiles((current) => current.filter((f) => f.id !== id));
    },
    [applyFiles],
  );

  return (
    <div className="flex flex-col gap-3">
      <input
        {...getInputProps()}
        accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.png,.jpeg,.jpg"
        className="hidden"
        multiple
        type="file"
      />
      <label
        {...getRootProps()}
        className={twMerge(
          'relative flex w-full cursor-pointer items-center gap-6 rounded-lg border border-dashed border-gray-200 p-6',
          // At the cap the dropzone is disabled, so the block itself explains why instead of
          // leaving a dead control (86cat0jym).
          message && 'flex-col items-center gap-2 border-fireside-200 text-center',
          filesFirst && 'order-2',
        )}
      >
        {message ? (
          <>
            <InfoCircle className="size-12" iconColor="#D92D20" thinIconVariant />
            <span className="text-center text-sm font-medium text-fireside-600">{message}</span>
            <span className="text-center text-[10px] font-medium text-gray-500 opacity-70">
              <AppFormattedMessage id="DIVIDEND_FILE_TYPES_HINT" />
              <br />
              <AppFormattedMessage id="MAX_FILE_SIZE_10_MB" />
            </span>
            {!isAtMax && (
              <span className="text-sm font-medium text-gray-700 underline">
                <AppFormattedMessage id="CLICK_TO_UPLOAD" />
              </span>
            )}
          </>
        ) : (
          <>
            <CloudUploadIcon />
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-gray-700">
                <span className="underline">
                  <AppFormattedMessage id="CLICK_TO_UPLOAD" />
                </span>{' '}
                <AppFormattedMessage id="OR_DRAG_AND_DROP" />
              </p>
              <span className="text-[10px] font-medium text-gray-500 opacity-70">
                <AppFormattedMessage id="DIVIDEND_FILE_TYPES_HINT" />
                <br />
                <AppFormattedMessage id="MAX_FILE_SIZE_10_MB" />
              </span>
            </div>
          </>
        )}
      </label>

      {files.length > 0 && (
        <div className={`flex flex-col gap-2 ${filesFirst ? 'order-1' : ''}`}>
          {counter && <span className="text-xs font-[450] text-gray-500">{counter}</span>}
          {files.map((file) => (
            <div
              className="flex flex-col overflow-hidden rounded border border-brand-50 bg-brand-25"
              key={file.id}
            >
              <div className="flex items-center justify-between gap-2 p-3">
                {/* `min-w-0` lets the name truncate instead of pushing the icon and the remove
                    button out of the row — a flex child refuses to shrink below its content. */}
                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                  <span className="flex shrink-0 items-center">
                    {file.docLink ? <UploadedFileIcon /> : <LoaderIcon className="size-4" />}
                  </span>
                  <span className="truncate text-xs font-[450] text-gray-700">{file.name}</span>
                </div>
                <Button
                  className="w-fit shrink-0 p-1 hover:bg-gray-50"
                  onClick={() => handleDelete(file.id)}
                  styleType="NONE"
                  type="button"
                >
                  <CloseIcon className="size-2" />
                </Button>
              </div>
              {/* Progress bar belongs to the upload itself — a finished file is a plain chip. */}
              {!file.docLink && (
                <div
                  className="h-[2px] shrink-0 bg-brand-700 transition-all duration-700"
                  style={{ width: `${file.loadProgress}%` }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
